/**
 * `@`-trigger detection shared between the Lexical body editor and the
 * reference text input, so both open the mention picker on exactly the same
 * keystrokes. Mirrors the matcher in
 * `lexical-rich-text-editor/mention-plugin.tsx`.
 *
 * `PUNCTUATION` is inlined (verbatim from `@lexical/react`'s
 * `LexicalTypeaheadMenuPlugin`) rather than imported, so this module — and the
 * plain reference text input that depends on it — stays free of any Lexical
 * runtime dependency.
 */

// eslint-disable-next-line no-useless-escape
const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';

export interface AtSignMatch {
  /** Index of the `@` that begins the trigger, within the searched text. */
  leadOffset: number;
  /** The query typed after the `@`. */
  matchingString: string;
  /** The full `@query` substring (what is replaced when a mention is picked). */
  replaceableString: string;
}

const TRIGGER = '@';
// Query characters exclude the trigger and sentence punctuation (so punctuation
// closes the query) but DO allow spaces, so a query can span multiple words.
// Newlines still terminate the query — important for the multi-line Lexical
// editor that shares this matcher (a single-line input never contains one).
const VALID_CHARS = `[^${TRIGGER}${PUNCTUATION}\\n\\r]`;
const LENGTH_LIMIT = 75;

// A `@` is a valid trigger at start-of-string, after whitespace, or after
// opening punctuation: ( [ { < straight quotes, or smart quotes.
const OPENING = `[(\\[{<'"\\u2018\\u2019\\u201C\\u201D]`;

// `@` preceded by a valid opener, followed by up to 75 query characters,
// anchored to the end of the input.
const AT_SIGN_REGEX = new RegExp(
  `(^|\\s|${OPENING})([${TRIGGER}]((?:${VALID_CHARS}){0,${LENGTH_LIMIT}}))$`
);

export function checkForAtSignMentions(
  text: string,
  minMatchLength = 1
): AtSignMatch | null {
  const match = AT_SIGN_REGEX.exec(text);
  if (match === null) return null;
  const matchingString = match[3];
  // Eligibility counts searchable (non-whitespace) characters only, so a bare
  // `@` — or `@` followed solely by spaces — does not open the picker.
  if (matchingString.trim().length < minMatchLength) return null;
  return {
    leadOffset: match.index + match[1].length,
    matchingString,
    replaceableString: match[2],
  };
}
